import { useModals } from 'almanac-ui/Modal'
import cx from 'classnames'
import Styles from './FontsUploadModal.module.scss'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone'
import { useRef } from 'react'
import { Button as LegacyButton } from 'components/Button'
import { UploadCloud } from 'almanac-ui/icons'
import { ConfirmFontModal } from 'components/ConfirmFontModal/ConfirmFontModal'
import { UploadCloud as UploadCloud24 } from 'almanac-ui/icons/24'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { Text } from 'blaze-ui/Text/Text'
import { Upload5 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'

const DragActive = () => (
  <div className={Styles.dropPrompt}>
    <div className={Styles.roundIcon}>
      <UploadCloud24 color="var(--focus-50)" />
    </div>
    <p className={Styles.dropZoneText}>Drop here to upload</p>
  </div>
)

export const FontsUploadDropzone = () => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { openModal, closeModal } = useModals()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isMobile } = useResponsiveDesign()

  const handleDrop = async (files: File[], rejectedFiles: FileRejection[], event: DropEvent) => {
    event.preventDefault()

    if (rejectedFiles.length > 0) {
      let message = ''
      rejectedFiles.forEach((rejectedFile) => {
        rejectedFile.errors.forEach((fileError) => {
          message += fileError.message + '\n'
        })
      })
      showErrorMessageToast(`Unable to upload some files: ${message}`)
    }
    if (files.length > 0) {
      closeModal()
      openModal(ConfirmFontModal, { files })
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: ['.otf', '.ttf', '.woff'],
    multiple: true,
  })

  return (
    <div id="drop-zone" className={cx(Styles.dropZone, { [Styles.dragActive]: isDragActive })} {...getRootProps()}>
      <div className={Styles.dropZoneContent}>
        <VisuallyHidden>
          <input {...getInputProps()} ref={fileInputRef} />
        </VisuallyHidden>
        {isDragActive && <DragActive />}
        {!isDragActive && (
          <div className={Styles.description}>
            {isSystemRedesignEnabled ? (
              <div className={Styles.dropZoneTextContainer}>
                <div className={Styles.dropZoneText}>
                  <Text color="var(--dark-80)" variant="secondary">
                    <Upload5 size={20} /> Drag & Drop fonts here
                  </Text>
                  <Text color="var(--dark-30)" variant="label">
                    Supported files: OFT, TTF, WOFF
                  </Text>
                </div>
                <Button onPress={() => fileInputRef.current?.click()} size={isMobile ? 'lg' : 'md'}>
                  Choose Font Files
                </Button>
              </div>
            ) : (
              <>
                <span className={Styles.dropText}>
                  Drag & Drop font files here
                  <br /> Supported file formats: otf, ttf, woff
                </span>
                <LegacyButton
                  onPress={() => fileInputRef.current?.click()}
                  variant="primary"
                  size={isMobile ? 'lg' : 'md'}
                  frontIcon={UploadCloud}
                >
                  Choose Files
                </LegacyButton>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
